import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./TearlinePaywall.module.scss"

/**
 * TearlinePaywall (`/tearline`) — a self-contained NON-ONBOARDING upsell for Private
 * Photo Vault built as a PERFORATED ROLL OF DAYS. Your days come off an endless paper
 * roll like a strip of stamps — each one a photo. A whole YEAR of them sits as a
 * kraft-paper stub joined to the roll by a dashed perforation. You grab the pull-tab
 * and RIP down the perforation; the year tears free, drops into the "kept" tray, a
 * blue KEPT seal thunks onto it, and the price resolves to its true cost PER DAY —
 * about a dime. That per-day figure is the whole pitch: a year of every memory,
 * protected, for pennies a day — and the yearly rate is a fraction of monthly's daily
 * cost.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/tearline.md
 *
 * NEW mechanic (not in the ledger): PERFORATION TEAR / RIP-A-STUB. A progressive rip
 * down a dashed seam that detaches a piece of paper. Not a pour, scrub, flip, dial,
 * hold, lasso, stamp-press, key-wind, haul, crank, boot, pinch or slider — and NOT a
 * grey→colour reveal (tiles are full colour throughout; tearing changes only whether
 * the year is CLAIMED, shown by the blue seal, not by colour).
 *
 * NEW layout: a horizontal TICKET/STAMP ROLL with sprocket holes and a vertical
 * perforation — no centred hero card, no before/after split, no vessel, no grid-only.
 *
 * Value frame (rarely used — price-anchoring, 1× before this) fused with FUTURE-SELF:
 * the honest engine for driving YEARLY. The mechanic literally converts the yearly
 * price into a per-day figure and contrasts it with monthly's higher per-day cost.
 * Register: breezy, generous, practical-optimistic "a dime a day" — distinct from the
 * fear / luxury / reverent / mechanical / consolidation registers of prior concepts.
 *
 * Honest & App Store compliant: yearly is the featured plan and carries the most
 * prominent price ($/yr) plus its exact per-day math; monthly is a small secondary
 * line with its own honest per-day figure. The per-day and savings numbers are exact
 * arithmetic off the real debug products (yearly ÷ 365; monthly × 12 ÷ 365). No
 * countdown, no fake scarcity, no misleading savings claim. Copy never implies photos
 * are lost when a plan ends — it says the opposite. Features are real Premium features.
 * Photos are bundled repo images (no network egress).
 */

type PlanKey = "yearly" | "monthly"

// twelve months of a year — bundled everyday photos already in the repo, no egress
const MONTHS = [
  { m: "JAN", src: "/recall/mountain-1.jpg", pos: "50% 50%" },
  { m: "FEB", src: "/recall/coffee-1.jpg", pos: "50% 50%" },
  { m: "MAR", src: "/recall/flower-1.jpg", pos: "50% 45%" },
  { m: "APR", src: "/recall/city-1.jpg", pos: "50% 50%" },
  { m: "MAY", src: "/recall/dog-2.jpg", pos: "50% 40%" },
  { m: "JUN", src: "/recall/beach-2.jpg", pos: "50% 55%" },
  { m: "JUL", src: "/recall/sunset-1.jpg", pos: "50% 45%" },
  { m: "AUG", src: "/recall/food-1.jpg", pos: "50% 50%" },
  { m: "SEP", src: "/recall/flower-2.jpg", pos: "50% 45%" },
  { m: "OCT", src: "/recall/mountain-2.jpg", pos: "50% 50%" },
  { m: "NOV", src: "/recall/coffee-2.jpg", pos: "50% 50%" },
  { m: "DEC", src: "/recall/city-2.jpg", pos: "50% 50%" },
]

// the roll drifting past the tear line into "days to come" — faint, future
const FUTURE = [
  "/recall/beach-3.jpg",
  "/recall/dog-3.jpg",
  "/recall/sunset-2.jpg",
  "/recall/food-2.jpg",
  "/recall/flower-3.jpg",
  "/recall/mountain-3.jpg",
]

// real Premium features — compact secondary line (not the hero)
const FEATURES = [
  { icon: "cloud", label: "Backed up automatically" },
  { icon: "hd", label: "Full-quality originals" },
  { icon: "lock", label: "Private & encrypted" },
  { icon: "devices", label: "On every device" },
]

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

const Icon = ({ name, size = 15 }: { name: string; size?: number }) => {
  const c = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      {name === "lock" && (
        <>
          <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" {...c} />
          <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" {...c} />
        </>
      )}
      {name === "hd" && (
        <>
          <rect x="3.5" y="5" width="17" height="14" rx="2.4" {...c} />
          <path d="M7 9.5v5M7 12h2.4M9.4 9.5v5" {...c} />
          <path
            d="M12.8 9.5v5h1.8a2 2 0 0 0 2-2v-1a2 2 0 0 0-2-2h-1.8Z"
            {...c}
          />
        </>
      )}
      {name === "cloud" && (
        <path
          d="M7 18a4 4 0 0 1-.4-8 5.5 5.5 0 0 1 10.6 1.2A3.4 3.4 0 0 1 17 18H7Z"
          {...c}
        />
      )}
      {name === "devices" && (
        <>
          <rect x="3" y="6" width="12" height="9" rx="1.6" {...c} />
          <rect x="16" y="9" width="5" height="9" rx="1.4" {...c} />
          <path d="M6 18h6" {...c} />
        </>
      )}
      {name === "shield" && (
        <path
          d="M12 2.6l7 2.7v5.2c0 4.6-3 8.2-7 10.9-4-2.7-7-6.3-7-10.9V5.3l7-2.7Z"
          {...c}
        />
      )}
      {name === "check" && <path d="M5 12.5l4.2 4.2L19 7" {...c} />}
      {name === "arrow" && (
        <>
          <path d="M5 12h13" {...c} />
          <path d="M12.5 6l6 6-6 6" {...c} />
        </>
      )}
    </svg>
  )
}

const TearlinePaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data; yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPriceNum = yearlyProduct?.price ?? 35.99
  const monthlyPriceNum = monthlyProduct?.price ?? 13.99

  const yearlyPrice = formatCurrency(yearlyPriceNum, locale)
  const monthlyPrice = formatCurrency(monthlyPriceNum, locale)
  const yearlyPerDay = formatCurrency(yearlyPriceNum / 365, locale)
  const monthlyPerDay = formatCurrency((monthlyPriceNum * 12) / 365, locale)

  const yearlyTrial = yearlyProduct?.hasTrial
    ? (yearlyProduct?.introPricing?.period ?? 7)
    : 0
  const monthlyTrial = monthlyProduct?.hasTrial
    ? (monthlyProduct?.introPricing?.period ?? 3)
    : 0

  // how many times more you pay per day on monthly vs yearly — exact, honest
  const perDayRatio = useMemo(() => {
    const y = yearlyPriceNum / 365
    const m = (monthlyPriceNum * 12) / 365
    if (!y) return 0
    return Math.round((m / y) * 10) / 10
  }, [yearlyPriceNum, monthlyPriceNum])

  const savingsPct = useMemo(() => {
    if (!monthlyPriceNum) return 0
    return Math.max(
      0,
      Math.round((1 - yearlyPriceNum / (monthlyPriceNum * 12)) * 100),
    )
  }, [yearlyPriceNum, monthlyPriceNum])

  // ── tear state ──────────────────────────────────────────────────────────────
  const [rip, setRip] = useState(0) // 0..1 how far the perforation has torn
  const [torn, setTorn] = useState(false) // year fully torn free & kept
  const [engaged, setEngaged] = useState(false)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)

  const seamRef = useRef<HTMLDivElement | null>(null)
  const ripRef = useRef(0)
  const dragRef = useRef<{ y: number; rip: number } | null>(null)
  const autoRef = useRef<number | null>(null)

  const setRipClamped = useCallback((v: number) => {
    const nv = clamp(v, 0, 1)
    ripRef.current = nv
    setRip(nv)
    if (nv >= 1) {
      setTorn((was) => {
        if (!was) native.logMixpanelEvent("html_tearline_torn", {})
        return true
      })
    }
  }, [])

  // reduced motion — arrive already torn & kept, no animation
  useEffect(() => {
    if (!reduceMotion) return
    ripRef.current = 1
    setRip(1)
    setTorn(true)
    setEngaged(true)
    setShowClose(true)
  }, [reduceMotion])

  // reveal close after a beat (not tied to the tear)
  useEffect(() => {
    if (reduceMotion) return
    const t = window.setTimeout(() => setShowClose(true), 1300)
    return () => window.clearTimeout(t)
  }, [reduceMotion])

  // colour the safe-area strips to the paper
  useEffect(() => {
    actions.setHeaderInsetColor("#f6efe1")
    actions.setFooterInsetColor("#ffffff")
  }, [actions])

  // ── input: rip the perforation ──────────────────────────────────────────────
  const onPointerDown = (e: React.PointerEvent) => {
    if (torn) return
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    dragRef.current = { y: e.clientY, rip: ripRef.current }
    if (!engaged) {
      setEngaged(true)
      native.logMixpanelEvent("html_tearline_grab", {})
    }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const base = dragRef.current
    if (!base || torn) return
    const seam = seamRef.current
    const span = seam ? Math.max(120, seam.getBoundingClientRect().height) : 220
    const dy = e.clientY - base.y // pull DOWN to rip
    setRipClamped(base.rip + dy / span)
  }
  const onPointerUp = () => {
    dragRef.current = null
    // spring back if they didn't commit past the point of no return
    if (!torn && ripRef.current < 0.62 && !reduceMotion) {
      const from = ripRef.current
      const t0 = performance.now()
      const back = (now: number) => {
        const k = clamp((now - t0) / 260, 0, 1)
        setRipClamped(from * (1 - k))
        if (k < 1) requestAnimationFrame(back)
      }
      requestAnimationFrame(back)
    } else if (!torn) {
      // committed — finish the rip smoothly
      const from = ripRef.current
      const t0 = performance.now()
      const go = (now: number) => {
        const k = clamp((now - t0) / 220, 0, 1)
        setRipClamped(from + (1 - from) * k)
        if (k < 1) requestAnimationFrame(go)
      }
      requestAnimationFrame(go)
    }
  }

  const tearAll = () => {
    if (torn) return
    setEngaged(true)
    native.logMixpanelEvent("html_tearline_tear_button", {})
    if (autoRef.current != null) cancelAnimationFrame(autoRef.current)
    const from = ripRef.current
    const t0 = performance.now()
    const dur = reduceMotion ? 0 : 520
    const go = (now: number) => {
      const k = dur ? clamp((now - t0) / dur, 0, 1) : 1
      // ease-out
      const e = 1 - Math.pow(1 - k, 2)
      setRipClamped(from + (1 - from) * e)
      if (k < 1) autoRef.current = requestAnimationFrame(go)
    }
    if (dur) autoRef.current = requestAnimationFrame(go)
    else setRipClamped(1)
  }

  const onSeamKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      tearAll()
    }
  }

  // ── plan + purchase wiring ──────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    if (!torn) {
      // let the CTA also complete the tear if tapped early
      tearAll()
    }
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    native.logMixpanelEvent("html_tearline_purchase", { plan })
    if (product) native.subscribe(product)
  }

  const trialDays = plan === "yearly" ? yearlyTrial : monthlyTrial
  const ctaSub =
    plan === "yearly"
      ? trialDays > 0
        ? `${trialDays} days free, then ${yearlyPrice}/yr · about ${yearlyPerDay}/day · cancel anytime`
        : `${yearlyPrice}/yr · about ${yearlyPerDay}/day · cancel anytime`
      : trialDays > 0
        ? `${trialDays} days free, then ${monthlyPrice}/mo · about ${monthlyPerDay}/day · cancel anytime`
        : `${monthlyPrice}/mo · about ${monthlyPerDay}/day · cancel anytime`

  // rip visuals
  const ripPct = Math.round(rip * 100)
  const hint = torn
    ? "Your year is yours"
    : engaged
      ? rip > 0.15
        ? "Keep pulling…"
        : "Pull down the tab to tear it off"
      : "Grab the tab and pull down ↓"

  return (
    <div className={styles.page}>
      <button
        className={`${styles.close} ${showClose ? styles.closeShown : ""}`}
        onClick={handleClose}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <span className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <Icon name="shield" size={13} />
          </span>
          Private Photo Vault
        </span>
        <h1 className={styles.title}>
          A whole year of memories,
          <br />
          for about {yearlyPerDay} a day.
        </h1>
        <p className={styles.sub}>
          Your days come off the roll one at a time. Tear off a year and keep
          every photo &amp; video in it — private, full-quality, backed up.
        </p>
      </header>

      {/* ── the perforated roll ──────────────────────────────────────────────── */}
      <section className={styles.stage}>
        <div
          className={`${styles.roll} ${torn ? styles.rollTorn : ""}`}
          style={{ ["--rip" as any]: rip }}
        >
          {/* the YEAR stub — what you tear off and keep */}
          <div className={styles.stub} aria-label="One year of memories">
            <div className={styles.stubTop}>
              <span className={styles.stubKicker}>KEEP THIS</span>
              <span className={styles.stubYear}>1 YEAR</span>
            </div>
            <div className={styles.months}>
              {MONTHS.map((mo, i) => (
                <span
                  key={mo.m}
                  className={styles.month}
                  style={{
                    backgroundImage: `url(${mo.src})`,
                    backgroundPosition: mo.pos,
                    ["--mi" as any]: i,
                  }}
                >
                  <span className={styles.monthTag}>{mo.m}</span>
                </span>
              ))}
              <span
                className={`${styles.seal} ${torn ? styles.sealOn : ""}`}
                aria-hidden
              >
                <Icon name="check" size={17} />
                KEPT
              </span>
            </div>
            <div className={styles.stubFoot}>
              <span className={styles.stubPrice}>
                <b>{yearlyPrice}</b>
                <i>/year</i>
              </span>
              <span className={styles.stubPerDay}>
                <b>{yearlyPerDay}</b>
                <i>a day</i>
              </span>
            </div>
          </div>

          {/* the perforation seam + pull tab */}
          <div
            className={styles.seam}
            ref={seamRef}
            role="button"
            tabIndex={0}
            aria-label="Tear off your year"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={onSeamKey}
          >
            <span className={styles.perf} aria-hidden />
            <span
              className={styles.ripEdge}
              aria-hidden
              style={{ ["--rip" as any]: rip }}
            />
            {!torn && (
              <span
                className={styles.tab}
                aria-hidden
                style={{ top: `calc(6px + ${ripPct}% * 0.78)` }}
              >
                <Icon name="arrow" size={15} />
              </span>
            )}
          </div>

          {/* the roll continuing into days to come */}
          <div className={styles.future} aria-hidden>
            <span className={styles.rollLabel}>days to come →</span>
            <div className={styles.futureStrip}>
              {FUTURE.map((src, i) => (
                <span
                  key={src}
                  className={styles.futureDay}
                  style={{
                    backgroundImage: `url(${src})`,
                    ["--fi" as any]: i,
                  }}
                />
              ))}
            </div>
          </div>

          {/* sprocket holes top & bottom, film-roll style */}
          <span
            className={`${styles.sprockets} ${styles.sprocketsTop}`}
            aria-hidden
          >
            {Array.from({ length: 22 }).map((_, i) => (
              <i key={i} />
            ))}
          </span>
          <span
            className={`${styles.sprockets} ${styles.sprocketsBottom}`}
            aria-hidden
          >
            {Array.from({ length: 22 }).map((_, i) => (
              <i key={i} />
            ))}
          </span>
        </div>

        <div className={styles.stageFoot}>
          {torn ? (
            <span className={styles.doneLine}>
              <Icon name="shield" size={14} />
              365 days · every photo &amp; video · kept in your vault
            </span>
          ) : (
            <>
              <span className={styles.hint}>{hint}</span>
              <button className={styles.tearBtn} onClick={tearAll}>
                Tear off my year
              </button>
            </>
          )}
        </div>
      </section>

      {/* per-day contrast — the honest value line that drives yearly */}
      <div className={`${styles.compare} ${torn ? styles.compareOn : ""}`}>
        <span className={styles.compareRow}>
          <span className={styles.compareTag}>Yearly</span>
          <span className={styles.bar}>
            <span
              className={styles.barFill}
              style={{ width: "18%" }}
              data-plan="yearly"
            />
          </span>
          <span className={styles.compareVal}>{yearlyPerDay}/day</span>
        </span>
        <span className={styles.compareRow}>
          <span className={`${styles.compareTag} ${styles.compareTagDim}`}>
            Monthly
          </span>
          <span className={styles.bar}>
            <span
              className={styles.barFill}
              style={{ width: "84%" }}
              data-plan="monthly"
            />
          </span>
          <span className={`${styles.compareVal} ${styles.compareValDim}`}>
            {monthlyPerDay}/day
          </span>
        </span>
        {perDayRatio > 1 && (
          <span className={styles.compareNote}>
            The yearly plan is the same vault for about{" "}
            <b>{perDayRatio}× less</b> per day — that&apos;s {savingsPct}% off
            over a year.
          </span>
        )}
      </div>

      <ul className={styles.features}>
        {FEATURES.map((f) => (
          <li key={f.label} className={styles.feature}>
            <Icon name={f.icon} size={15} />
            {f.label}
          </li>
        ))}
      </ul>

      {/* plans — yearly featured; monthly small & secondary */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.sel : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
          <span className={styles.planMain}>
            <span className={styles.radio} aria-hidden />
            <span className={styles.planNameWrap}>
              <span className={styles.planName}>Keep the year</span>
              <span className={styles.planNote}>
                {yearlyTrial > 0
                  ? `${yearlyTrial}-day free trial · about ${yearlyPerDay}/day`
                  : `about ${yearlyPerDay} a day, billed yearly`}
              </span>
            </span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.selMonth : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Or pay monthly</span>
          <span className={styles.planMonthPrice}>
            {monthlyPrice}/mo · {monthlyPerDay}/day
          </span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>
          {plan === "yearly" ? "Keep my year" : "Start monthly"}
        </span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours — nothing is ever deleted if a plan ends.
        Premium simply keeps a private, backed-up copy of every day safe.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default TearlinePaywall
